import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AsYouType, parsePhoneNumber } from 'libphonenumber-js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faCamera, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import { updateUserProfile, ensureUserProfileExists, subscribeToUserProfile } from '../../services/userService';
import { uploadProfilePhoto } from '../../services/storageService';
import { isPublicProfileComplete, updatePublicProfile, getPublicUserProfile } from '../../services/publicUserProfileService';
import type { PublicUserProfile, UserProfile as UserProfileType } from '../../types/user';
import ImageCropper from '../ImageCropper';
import UserLink from '../UserLink';
import { readFile } from '../../utils/imageUtils';
import './UserProfile.css';

interface UserProfileProps {
  onClose: () => void;
  onProfileStatusChange?: () => void; // New callback for profile status changes
}

export default function UserProfile({ onClose, onProfileStatusChange }: UserProfileProps) {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const [profile, setProfile] = useState<UserProfileType | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [pronouns, setPronouns] = useState('');
  const [bio, setBio] = useState('');
  const [location, setLocation] = useState('');
  const [showLocation, setShowLocation] = useState(false);
  const [phoneNumberError, setPhoneNumberError] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [imageToCrop, setImageToCrop] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [publicProfileData, setPublicProfileData] = useState<PublicUserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [creatingProfile, setCreatingProfile] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const saveTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  useEffect(() => {
    if (!currentUser) return;

    setLoading(true);
    setError('');

    // Subscribe to real-time profile updates
    const unsubscribe = subscribeToUserProfile(
      currentUser.uid,
      async (userProfile: UserProfileType | null) => {
        if (!userProfile) {
          // Profile not found - try to create it
          setCreatingProfile(true);
          console.log('Profile not found, attempting to create...');
          const createdProfile = await ensureUserProfileExists(currentUser);
          setCreatingProfile(false);

          if (!createdProfile) {
            setError(t('profile.errors.createFailed'));
            setLoading(false);
            return;
          }
          // Don't update state here - the subscription will trigger with the new profile
          return;
        }

        // Update local state with profile data
        setProfile(userProfile);
        setDisplayName(userProfile.profile.displayName || '');
        setPhoneNumber(userProfile.profile.phoneNumber || '');
        setSmsOptIn(userProfile.preferences.notifications?.smsOptIn ?? false);
        setDateOfBirth(userProfile.profile.dateOfBirth || '');
        setPronouns(userProfile.profile.pronouns || '');
        setBio(userProfile.profile.bio || '');
        setLocation(userProfile.profile.location || '');
        setShowLocation(userProfile.preferences.privacy?.showLocation ?? false);
        setLoading(false);
      },
      (err) => {
        console.error('Failed to subscribe to profile:', err);
        setError(t('profile.errors.loadFailed'));
        setLoading(false);
      }
    );

    // Cleanup subscription and timeout on unmount
    return () => {
      unsubscribe();
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [currentUser, t]);

  // Separate effect to fetch public profile when profile changes
  useEffect(() => {
    let isMounted = true;

    if (!profile) {
      setPublicProfileData(null);
      return;
    }

    const fetchPublicProfile = async () => {
      try {
        const publicProfile = await getPublicUserProfile(profile.uid);
        if (isMounted) {
          setPublicProfileData(publicProfile);
        }
      } catch (err) {
        console.error('Failed to load public profile:', err);
      }
    };

    fetchPublicProfile();

    return () => {
      isMounted = false;
    };
  }, [profile]);

  const validatePhoneNumber = (phoneNumber: string): boolean => {
    if (!phoneNumber || !phoneNumber.trim()) {
      return true; // Empty phone number is valid (optional field)
    }

    try {
      const parsed = parsePhoneNumber(phoneNumber, 'US');
      return parsed ? parsed.isValid() : false;
    } catch {
      return false;
    }
  };

  const saveFieldChange = async (field: 'displayName' | 'phoneNumber' | 'dateOfBirth' | 'pronouns' | 'bio' | 'location', value: string) => {
    if (!currentUser || !profile) return;

    // Clear any pending save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Debounce save by 1 second
    saveTimeoutRef.current = setTimeout(async () => {
      // Normalize empty values to empty string for comparison
      const normalizedValue = value.trim();
      const oldValue = (profile.profile[field] || '').toString();

      if (normalizedValue === oldValue) return;

      // Validate phone number before saving
      if (field === 'phoneNumber') {
        if (!validatePhoneNumber(normalizedValue)) {
          setPhoneNumberError(t('profile.phoneNumberInvalid'));
          return;
        }
        setPhoneNumberError('');
      }

      setSaving(true);
      setError('');

      try {
        // Create updated profile with new field value
        const updatedProfile = {
          ...profile,
          profile: { ...profile.profile, [field]: normalizedValue }
        };

        // Check if profile is now complete
        const wasComplete = profile.metadata.profileCompleted;
        const profileComplete = isPublicProfileComplete(updatedProfile);

        // Update profile with new field and completion status
        await updateUserProfile(currentUser.uid, {
          profile: { [field]: normalizedValue },
          metadata: { profileCompleted: profileComplete }
        });

        // Notify parent if profile completion status changed
        if (wasComplete !== profileComplete && onProfileStatusChange) {
          onProfileStatusChange();
        }

        // Update public profile if field is public-facing
        if (['displayName', 'bio', 'location'].includes(field)) {
          await updatePublicProfile(currentUser.uid, updatedProfile);
        }

        // Real-time subscription will update the UI automatically
        // Just show success message
        setSuccess(t('profile.updateSuccess'));
        setTimeout(() => setSuccess(''), 3000);
      } catch (err) {
        console.error('Failed to update profile:', err);
        setError(t('profile.errors.updateFailed'));
      } finally {
        setSaving(false);
      }
    }, 1000);
  };

  const handleDisplayNameChange = (value: string) => {
    setDisplayName(value);
    saveFieldChange('displayName', value);
  };

  const handlePhoneNumberChange = (value: string) => {
    // Format phone number as user types
    const formatter = new AsYouType('US');
    const formattedValue = formatter.input(value);

    setPhoneNumber(formattedValue);
    saveFieldChange('phoneNumber', formattedValue);
  };

  const handleDateOfBirthChange = (value: string) => {
    setDateOfBirth(value);
    saveFieldChange('dateOfBirth', value);
  };

  const handlePronounsChange = (value: string) => {
    setPronouns(value);
    saveFieldChange('pronouns', value);
  };

  const handleBioChange = (value: string) => {
    setBio(value);
    saveFieldChange('bio', value);
  };

  const handleLocationChange = (value: string) => {
    setLocation(value);
    saveFieldChange('location', value);
  };

  const handleShowLocationChange = async (checked: boolean) => {
    if (!currentUser || !profile) return;

    setShowLocation(checked);
    setSaving(true);
    setError('');

    try {
      await updateUserProfile(currentUser.uid, {
        preferences: { privacy: { showLocation: checked } }
      });
      const updatedProfile = {
        ...profile,
        preferences: {
          ...profile.preferences,
          privacy: { ...profile.preferences.privacy, showLocation: checked }
        }
      };
      setProfile(updatedProfile);

      // Update public profile to reflect privacy change
      await updatePublicProfile(currentUser.uid, updatedProfile);

      setSuccess(t('profile.updateSuccess'));
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Failed to update privacy settings:', err);
      setError(t('profile.errors.updateFailed'));
      setShowLocation(!checked); // Revert on error
    } finally {
      setSaving(false);
    }
  };

  const handleSmsOptInChange = async (checked: boolean) => {
    if (!currentUser || !profile) return;

    setSmsOptIn(checked);
    setSaving(true);
    setError('');

    try {
      await updateUserProfile(currentUser.uid, {
        preferences: { notifications: { smsOptIn: checked } }
      });

      setSuccess(t('profile.updateSuccess'));
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Failed to update SMS opt-in:', err);
      setError(t('profile.errors.updateFailed'));
      setSmsOptIn(!checked); // Revert on error
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !currentUser) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (10MB for original before cropping)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setError('Image must be smaller than 10MB');
      return;
    }

    setError('');

    try {
      // Read the file and show the cropper
      const imageDataUrl = await readFile(file);
      setImageToCrop(imageDataUrl);
    } catch (err) {
      console.error('Failed to read image file:', err);
      setError('Failed to read image file');
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCropComplete = async (croppedImageBlob: Blob) => {
    if (!currentUser || !profile) return;

    setImageToCrop(null);
    setUploadingPhoto(true);

    try {
      // Convert blob to file
      const croppedFile = new File([croppedImageBlob], 'avatar.jpg', {
        type: 'image/jpeg'
      });

      // Upload the cropped photo
      const downloadURL = await uploadProfilePhoto(currentUser.uid, croppedFile);

      // Create updated profile with new photo
      const updatedProfile = {
        ...profile,
        profile: { ...profile.profile, photoURL: downloadURL }
      };

      // Check if profile is now complete
      const wasComplete = profile.metadata.profileCompleted;
      const profileComplete = isPublicProfileComplete(updatedProfile);

      // Update the photo URL in the profile
      await updateUserProfile(currentUser.uid, {
        profile: { photoURL: downloadURL },
        metadata: { profileCompleted: profileComplete }
      });

      // Notify parent if profile completion status changed
      if (wasComplete !== profileComplete && onProfileStatusChange) {
        onProfileStatusChange();
      }

      // Update public profile
      await updatePublicProfile(currentUser.uid, updatedProfile);

      // Real-time subscription will update the UI automatically
      setSuccess('Profile photo updated successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Failed to upload photo:', err);
      setError(err instanceof Error ? err.message : 'Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleCropCancel = () => {
    setImageToCrop(null);
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  if (loading) {
    return (
      <div className="user-profile">
        <div className="profile-loading">
          {creatingProfile ? t('profile.creatingProfile') : t('profile.loading')}
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="user-profile">
        <div className="profile-error">
          <p>{t('profile.errors.notFound')}</p>
          <button
            className="retry-button"
            onClick={() => window.location.reload()}
          >
            {t('profile.retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="user-profile">
      {imageToCrop && (
        <ImageCropper
          imageSrc={imageToCrop}
          onCropComplete={handleCropComplete}
          onCancel={handleCropCancel}
          aspectRatio={1}
          shape="round"
        />
      )}

      {error && <div className="profile-error">{error}</div>}
      {success && <div className="profile-success">{success}</div>}

      <div className="profile-form">
        <div className="profile-header">
          <div className='profile-avatar-container'>
            <span className={`avatar-indicator field-indicator ${!profile.profile.photoURL?.trim() ? 'requested' : 'complete'}`} title="Required to complete profile"></span>
            <div className="profile-avatar" onClick={handleAvatarClick}>
              {profile?.profile.photoURL ? (
                <img src={profile.profile.photoURL} alt={displayName || 'User'} className="avatar-image" />
              ) : (
                <div className="avatar-placeholder">
                  <FontAwesomeIcon icon={faUser} className="avatar-icon" />
                </div>
              )}
              <div className="avatar-overlay">
                {uploadingPhoto ? (
                  <FontAwesomeIcon icon={faSpinner} spin className="upload-icon" />
                ) : (
                  <FontAwesomeIcon icon={faCamera} className="upload-icon" />
                )}
              </div>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handlePhotoUpload}
              className="hidden-file-input"
              disabled={uploadingPhoto || saving}
              aria-label="Upload profile photo"
            />
          </div>
          <div className="profile-name-section">
            <h3 className="profile-display-name">{displayName || profile?.profile.email}</h3>
            <p className="profile-email">{profile?.profile.email}</p>
          </div>
        </div>

        <div className="profile-public-link">
          <span>{t('userProfile.profileLink')}:</span>{' '}
          <UserLink uid={profile.uid} username={publicProfileData?.username}>
            {t('userProfile.viewPublicProfile')}
          </UserLink>
        </div>

        <div className={`form-group ${!profile.profile.displayName?.trim() ? 'required' : 'complete'}`}>
          <label htmlFor="displayName" className="required">
            {t('profile.displayName')}
            {!profile.profile.displayName?.trim() && <span className="field-indicator" title="Required field"></span>}
          </label>
          <input
            type="text"
            id="displayName"
            value={displayName}
            onChange={(e) => handleDisplayNameChange(e.target.value)}
            disabled={saving || uploadingPhoto}
            placeholder="Your display name"
          />
        </div>

        <div className={`form-group ${!profile.profile.phoneNumber?.trim() ? 'requested' : 'complete'}`}>
          <label htmlFor="phoneNumber" className="required">
            {t('profile.phoneNumber')}
            <span className={`field-indicator ${!profile.profile.phoneNumber?.trim() ? 'requested' : 'complete'}`}
              title="Required for security"></span>
          </label>
          <input
            type="tel"
            id="phoneNumber"
            value={phoneNumber}
            onChange={(e) => handlePhoneNumberChange(e.target.value)}
            disabled={saving || uploadingPhoto}
            placeholder="+1 (555) 123-4567"
          />
          <p className="field-hint">{t('profile.phoneNumberHint')}</p>
          {phoneNumberError && <p className="error-message">{phoneNumberError}</p>}
        </div>

        <div className="form-checkbox-group">
          <label className="checkbox-label" htmlFor='smsOptIn'>
            <input
              type="checkbox"
              id="smsOptIn"
              checked={smsOptIn}
              onChange={(e) => handleSmsOptInChange(e.target.checked)}
              disabled={saving || uploadingPhoto || !phoneNumber.trim()}
              className="checkbox-input"
            />
            <span className="checkbox-text">{t('profile.smsOptIn')}</span>
            <span className={`field-indicator ${!profile.preferences?.notifications?.smsOptIn ? 'requested' : 'complete'}`}
              title="Enable for best functionality. You can configure notifications."></span>
          </label>
          <p className="field-hint">{t('profile.smsOptInHint')}</p>
        </div>

        <div className={`form-group ${!profile.profile.dateOfBirth?.trim() ? 'requested' : 'complete'}`}
          title="Helps match voter profile and interests">
          <label htmlFor="dateOfBirth">
            {t('profile.dateOfBirth')}
          </label>
          <input
            type="date"
            id="dateOfBirth"
            value={dateOfBirth}
            onChange={(e) => handleDateOfBirthChange(e.target.value)}
            disabled={saving || uploadingPhoto}
          />
          <p className="field-hint">{t('profile.dateOfBirthHint')}</p>
        </div>

        <div className={`form-group ${!profile.profile.pronouns?.trim() ? 'requested' : 'complete'}`}
          title="Helps others address you correctly and makes everyone feel welcome">
          <label htmlFor="pronouns">
            {t('profile.pronouns')}
          </label>
          <input
            type="text"
            id="pronouns"
            value={pronouns}
            onChange={(e) => handlePronounsChange(e.target.value)}
            disabled={saving || uploadingPhoto}
            placeholder={t('profile.pronounsPlaceholder')}
          />
          <p className="field-hint">{t('profile.pronounsHint')}</p>
        </div>

        <div className={`form-group ${!profile.profile.bio?.trim() ? 'requested' : 'complete'}`}
          title="Please tell us a little about yourself. This will be shown on your public profile.">
          <label htmlFor="bio">
            {t('profile.bio')}
          </label>
          <textarea
            id="bio"
            value={bio}
            onChange={(e) => handleBioChange(e.target.value)}
            disabled={saving || uploadingPhoto}
            placeholder={t('profile.bioPlaceholder')}
            rows={4}
          />
          <p className="field-hint">{t('profile.bioHint')}</p>
        </div>

        <div className={`form-group ${!profile.profile.location?.trim() ? 'requested' : 'complete'}`}
          title="Please tell us where you live. City and state is sufficient. Voter registration addresses are better.">
          <label htmlFor="location">
            {t('profile.location')}
          </label>
          <input
            type="text"
            id="location"
            value={location}
            onChange={(e) => handleLocationChange(e.target.value)}
            disabled={saving || uploadingPhoto}
            placeholder={t('profile.locationPlaceholder')}
          />
          <p className="field-hint">{t('profile.locationHint')}</p>
        </div>

        <div className="privacy-section">
          <h4>{t('profile.privacySettings')}</h4>
          <p className="section-description">{t('profile.publicProfileDescription')}</p>

          <div className="privacy-item">
            <label className="privacy-label-info">
              {t('profile.publicFields.displayName')}
            </label>
          </div>

          <div className="privacy-item">
            <label className="privacy-label-info">
              {t('profile.publicFields.photoURL')}
            </label>
          </div>

          <div className="privacy-item">
            <label className="privacy-label-info">
              {t('profile.publicFields.bio')}
            </label>
          </div>

          <div className="privacy-item">
            <label className="checkbox-label" htmlFor="showLocation">
              <input
                type="checkbox"
                id="showLocation"
                checked={showLocation}
                onChange={(e) => handleShowLocationChange(e.target.checked)}
                disabled={saving || uploadingPhoto || !location}
                className="checkbox-input"
              />
              {t('profile.publicFields.location')}
            </label>
          </div>
        </div>

        <div className="profile-info">
          <p className="info-text">
            {t('profile.memberSince')}: {new Date(profile.metadata.createdAt).toLocaleDateString()}
          </p>
        </div>

        <div className="profile-actions">
          <button type="button" className="close-button" onClick={onClose}>
            {t('profile.close')}
          </button>
        </div>
      </div>
    </div>
  );
}
